import React, { useState } from 'react';
/**
 * Governance Rules — Sub-components
 *
 * RuleCard, RulebookCard, and ThresholdSection used by GovernanceRulesTab.
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */
import { XCircleIcon, AlertTriangleIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
import { DEFAULT_POLICY } from '../../../../governance';
import type { Severity, Stage, Rule, RulebookFile } from './governanceRulesData';
import { STAGE_LABELS, STAGE_COLORS, RULEBOOK_COLOR_MAP } from './governanceRulesData';
// ─── Severity Config ──────────────────────────────────────────
const SEVERITY_CONFIG: Record<Severity, {
  icon: React.ReactNode;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  BLOCKER: {
    icon: <XCircleIcon className="h-4 w-4" data-id="element-3900" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Blokkerend'
  },
  WARNING: {
    icon: <AlertTriangleIcon className="h-4 w-4" data-id="element-3901" />,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Waarschuwing'
  },
  INFO: {
    icon: <InfoIcon className="h-4 w-4" data-id="element-3902" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Informatief'
  }
};
// ─── RuleCard ─────────────────────────────────────────────────
export function RuleCard({
  rule
}: {
  rule: Rule;
}) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[rule.severity];
  return <div className={`rounded-lg border ${sev.border} bg-white overflow-hidden`} data-id="element-3903">
      <button className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-3904">
        <div className={`mt-0.5 flex-shrink-0 ${sev.text}`} data-id="element-3905">{sev.icon}</div>
        <div className="min-w-0 flex-1" data-id="element-3906">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3907">
            <code className="text-xs font-mono text-gray-400" data-id="element-3908">{rule.id}</code>
            <span className="font-semibold text-gray-800" data-id="element-3909">{rule.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[rule.stage]}`} data-id="element-3910">
              {STAGE_LABELS[rule.stage]}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sev.bg} ${sev.text}`} data-id="element-3911">
              {sev.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600" data-id="element-3912">{rule.description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-3913">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3914" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3915" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${sev.border} ${sev.bg} px-4 py-3 space-y-3`} data-id="element-3916">
          {rule.example && <div data-id="element-3917">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-3918">
                Bron / Voorbeeld
              </p>
              <code className="block rounded bg-white/70 px-3 py-2 text-xs text-gray-700 border border-gray-200" data-id="element-3919">
                {rule.example}
              </code>
            </div>}
          {rule.fix && <div data-id="element-3920">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-3921">
                Oplossing
              </p>
              <p className="text-sm text-gray-700" data-id="element-3922">{rule.fix}</p>
            </div>}
        </div>}
    </div>;
}
// ─── RulebookCard ─────────────────────────────────────────────
export function RulebookCard({
  book
}: {
  book: RulebookFile;
}) {
  const [expanded, setExpanded] = useState(false);
  const c = RULEBOOK_COLOR_MAP[book.color] ?? RULEBOOK_COLOR_MAP.blue;
  return <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-id="element-3923">
      <button className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)} data-id="element-3924">
        <FileTextIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${c.badge.split(' ')[1]}`} data-id="element-3925" />
        <div className="min-w-0 flex-1" data-id="element-3926">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-3927">
            <span className="font-semibold text-gray-800" data-id="element-3928">{book.name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.badge}`} data-id="element-3929">
              {book.module}
            </span>
          </div>
          <code className="text-xs text-gray-400 font-mono" data-id="element-3930">{book.path}</code>
          <p className="text-sm text-gray-600 mt-1" data-id="element-3931">{book.description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-3932">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3933" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3934" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${c.border} ${c.bg} px-4 py-3`} data-id="element-3935">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2" data-id="element-3936">
            Kernregels
          </p>
          <ul className="space-y-1.5" data-id="element-3937">
            {book.keyRules.map((rule, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3938">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" data-id="element-3939" />
                {rule}
              </li>)}
          </ul>
        </div>}
    </div>;
}
// ─── ThresholdSection ─────────────────────────────────────────
export function ThresholdSection() {
  const gates = DEFAULT_POLICY.gates;
  const budgets = DEFAULT_POLICY.contentBudgets;
  const flags = DEFAULT_POLICY.featureFlags;
  const thresholdItems = [{
    label: 'Productie min. score',
    value: `${gates.productionMinScore}%`,
    description: 'Vragen onder deze score worden geblokkeerd voor productie (oefenmodus).',
    path: 'gates.productionMinScore',
    color: gates.productionMinScore >= 70 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Assessment min. score',
    value: `${gates.assessmentMinScore}%`,
    description: 'Vragen onder deze score zijn niet geschikt voor CITO-stijl toetsen.',
    path: 'gates.assessmentMinScore',
    color: gates.assessmentMinScore >= 80 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Review drempel (coach ernst)',
    value: `≥ ${gates.reviewCoachSeverityThreshold}`,
    description: 'Coach-issues met ernst ≥ deze waarde triggeren een review-markering.',
    path: 'gates.reviewCoachSeverityThreshold',
    color: 'text-blue-700'
  }, {
    label: 'Min. vragen per family (assessment)',
    value: `${gates.assessmentMinQuestionsPerFamily}`,
    description: 'Families met minder vragen zijn niet assessment-geschikt.',
    path: 'gates.assessmentMinQuestionsPerFamily',
    color: 'text-blue-700'
  }];
  const budgetItems = [{
    label: 'Min. uitleglengte (basic)',
    value: `${budgets.minExplanationLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4 (B6/M6)',
    path: 'contentBudgets.minExplanationLengthByTierBand.basic'
  }, {
    label: 'Min. uitleglengte (intermediate)',
    value: `${budgets.minExplanationLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8 (E6/B7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.intermediate'
  }, {
    label: 'Min. uitleglengte (advanced)',
    value: `${budgets.minExplanationLengthByTierBand.advanced} tekens`,
    description: 'Tiers 9–12 (M7/E7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.advanced'
  }, {
    label: 'Min. uitleglengte (expert)',
    value: `${budgets.minExplanationLengthByTierBand.expert} tekens`,
    description: 'Tiers 13–16 (B8/E8+)',
    path: 'contentBudgets.minExplanationLengthByTierBand.expert'
  }, {
    label: 'Min. distractor-uitleg (basic)',
    value: `${budgets.minDistractorLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4',
    path: 'contentBudgets.minDistractorLengthByTierBand.basic'
  }, {
    label: 'Min. distractor-uitleg (intermediate)',
    value: `${budgets.minDistractorLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8',
    path: 'contentBudgets.minDistractorLengthByTierBand.intermediate'
  }];
  const featureFlagItems = Object.entries(flags).map(([key, value]) => ({
    key,
    value: value as boolean,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
  }));
  return <div className="space-y-6" data-id="element-3940">
      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-3941">
        <div className="flex items-start gap-3" data-id="element-3942">
          <InfoIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-3943" />
          <div data-id="element-3944">
            <p className="text-sm font-semibold text-blue-800" data-id="element-3945">
              Drempelwaarden aanpassen
            </p>
            <p className="text-sm text-blue-700 mt-0.5" data-id="element-3946">
              Drempelwaarden zijn gedefinieerd in{' '}
              <code className="font-mono bg-blue-100 px-1 rounded" data-id="element-3947">
                src/governance/pipeline/defaultPolicy.ts
              </code>
              . Pas het bestand direct aan om drempelwaarden te wijzigen.
              Gebruik de <strong data-id="element-3948">Instellingen</strong> tab voor
              runtime-aanpassingen.
            </p>
          </div>
        </div>
      </div>

      {/* Gate Thresholds */}
      <div data-id="element-3949">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3950">
          Kwaliteitspoorten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3951">
          {thresholdItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < thresholdItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3952">
              <div className="min-w-0" data-id="element-3953">
                <p className="text-sm font-medium text-gray-800" data-id="element-3954">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-3955">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-3956">
                  {item.path}
                </code>
              </div>
              <span className={`text-lg font-bold flex-shrink-0 ${item.color}`} data-id="element-3957">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Content Budgets */}
      <div data-id="element-3958">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3959">
          Inhoudsbudgetten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3960">
          {budgetItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < budgetItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3961">
              <div className="min-w-0" data-id="element-3962">
                <p className="text-sm font-medium text-gray-800" data-id="element-3963">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-3964">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-3965">
                  {item.path}
                </code>
              </div>
              <span className="text-sm font-bold text-gray-700 flex-shrink-0" data-id="element-3966">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Feature Flags */}
      <div data-id="element-3967">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3968">
          Feature Flags
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3969">
          {featureFlagItems.map((item, i) => <div key={item.key} className={`flex items-center justify-between gap-4 px-4 py-3 ${i < featureFlagItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3970">
              <div className="min-w-0" data-id="element-3971">
                <p className="text-sm font-medium text-gray-800" data-id="element-3972">
                  {item.label}
                </p>
                <code className="text-xs text-gray-400 font-mono" data-id="element-3973">
                  featureFlags.{item.key}
                </code>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold flex-shrink-0 ${item.value ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-3974">
                {item.value ? 'Actief' : 'Inactief'}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}